import {
  type ProspectDto,
  type ProspectStage,
  type ProspectType,
  useSalesAddAssignment,
  useSalesCompleteTask,
  useSalesCreateContact,
  useSalesCreateInteraction,
  useSalesCreateNote,
  useSalesCreateTask,
  useSalesDeleteContact,
  useSalesDeleteInteraction,
  useSalesDeleteNote,
  useSalesDeleteTask,
  useSalesGetProspect,
  useSalesRemoveAssignment,
  useSalesSetTimeZoneForProspect,
  useSalesUpdateContact,
  useSalesUpdateInteraction,
  useSalesUpdateNote,
  useSalesUpdateProspect,
  useSalesUpdateStage,
  useSalesUpdateTask,
} from "@crowdcoursing/api/openapi";
import { skipToken } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useSalesBubbleId } from "~hooks/useSalesBubbleId";
import { useToast } from "~hooks/useToast";

type ProspectUpdateField =
  | "name"
  | "type"
  | "estimatedValue"
  | "prospectType"
  | "expectedCloseDate"
  | "region"
  | "source"
  | "timeZone"
  | "website";

export interface ProspectDetailsOptimisticUpdates {
  name?: string;
  type?: string;
  stage?: ProspectStage;
  estimatedValue?: number;
  prospectType?: ProspectType;
  expectedCloseDate?: string;
  region?: string;
  source?: string;
  timeZone?: string;
  website?: string;
}

export const useProspectDetails = (prospectId: string) => {
  const toast = useToast();
  const salesBubbleId = useSalesBubbleId();
  const [optimisticUpdates, setOptimisticUpdates] = useState<ProspectDetailsOptimisticUpdates>({});

  // Fetch prospect data
  const {
    data: prospectData,
    isLoading,
    error,
    refetch,
  } = useSalesGetProspect(salesBubbleId ? { pathParams: { bubbleId: salesBubbleId, id: prospectId } } : skipToken);

  // Mutation hooks
  const { mutateAsync: updateProspect } = useSalesUpdateProspect();
  const { mutateAsync: updateStage } = useSalesUpdateStage();
  const { mutateAsync: setTimeZone } = useSalesSetTimeZoneForProspect();
  const { mutateAsync: addAssignment } = useSalesAddAssignment();
  const { mutateAsync: removeAssignment } = useSalesRemoveAssignment();
  const { mutateAsync: createContact } = useSalesCreateContact();
  const { mutateAsync: updateContact } = useSalesUpdateContact();
  const { mutateAsync: removeContact } = useSalesDeleteContact();
  const { mutateAsync: createNote } = useSalesCreateNote();
  const { mutateAsync: updateNote } = useSalesUpdateNote();
  const { mutateAsync: removeNote } = useSalesDeleteNote();
  const { mutateAsync: createInteraction } = useSalesCreateInteraction();
  const { mutateAsync: updateInteraction } = useSalesUpdateInteraction();
  const { mutateAsync: removeInteraction } = useSalesDeleteInteraction();
  const { mutateAsync: addTask } = useSalesCreateTask();
  const { mutateAsync: updateTask } = useSalesUpdateTask();
  const { mutateAsync: deleteTask } = useSalesDeleteTask();
  const { mutateAsync: completeTask } = useSalesCompleteTask();

  // Combine actual data with optimistic updates
  const prospect: ProspectDto | null = prospectData
    ? {
        ...prospectData,
        ...optimisticUpdates,
      }
    : null;

  // Header field updates
  const updateField = useCallback(
    async (field: ProspectUpdateField, value: string | number) => {
      if (!prospect || !salesBubbleId) {
        return;
      }

      // Apply optimistic update
      setOptimisticUpdates((prev) => ({ ...prev, [field]: value }));

      try {
        // Use dedicated endpoint for timeZone
        if (field === "timeZone") {
          await setTimeZone({
            pathParams: { bubbleId: salesBubbleId, prospectId },
            queryParams: { timezone: value as string },
          });
        } else {
          await updateProspect({
            body: { ...prospect, [field]: value },
            pathParams: { bubbleId: salesBubbleId, id: prospectId },
          });
        }
      } catch (err) {
        // Revert optimistic update on error
        setOptimisticUpdates((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
        toast.error("Failed to update prospect");
      }
    },
    [prospect, prospectId, salesBubbleId, setTimeZone, toast, updateProspect],
  );

  // Stage management
  const changeStage = useCallback(
    async (newStage: ProspectStage) => {
      if (!prospect || !salesBubbleId) {
        return;
      }

      // Apply optimistic update
      setOptimisticUpdates((prev) => ({ ...prev, stage: newStage }));

      try {
        await updateStage({
          body: { stage: newStage },
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        });
      } catch (err) {
        // Revert optimistic update on error
        setOptimisticUpdates((prev) => {
          const { stage: _, ...rest } = prev;
          return rest;
        });
        toast.error("Failed to update stage");
      }
    },
    [prospect, prospectId, salesBubbleId, toast, updateStage],
  );

  // Assignment management
  const assignUser = useCallback(
    async (userId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        // First add the assignment
        await addAssignment({
          body: { userId },
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        });

        // Then update the prospect to set the owner
        if (prospect) {
          await updateProspect({
            body: {
              ...prospect,
              ownerUserId: userId,
            },
            pathParams: { bubbleId: salesBubbleId, id: prospectId },
          });
        }

        await refetch();
      } catch (err) {
        toast.error("Failed to assign user");
      }
    },
    [prospectId, refetch, toast, addAssignment, updateProspect, prospect, salesBubbleId],
  );

  const unassignUser = useCallback(
    async (userId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await removeAssignment({
          pathParams: { bubbleId: salesBubbleId, id: prospectId, userId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to unassign user");
      }
    },
    [prospectId, refetch, toast, removeAssignment, salesBubbleId],
  );

  // Contact management
  const addNewContact = useCallback(
    async (contactData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await createContact({
          body: contactData,
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add contact");
      }
    },
    [prospectId, refetch, toast, createContact, salesBubbleId],
  );

  const updateExistingContact = useCallback(
    async (contactId: string, contactData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await updateContact({
          body: contactData,
          pathParams: { bubbleId: salesBubbleId, contactId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update contact");
      }
    },
    [prospectId, refetch, toast, updateContact, salesBubbleId],
  );

  const deleteContact = useCallback(
    async (contactId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await removeContact({
          pathParams: { bubbleId: salesBubbleId, contactId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete contact");
      }
    },
    [prospectId, refetch, toast, removeContact, salesBubbleId],
  );

  // Note management
  const addNewNote = useCallback(
    async (noteData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await createNote({
          body: noteData,
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add note");
      }
    },
    [prospectId, refetch, toast, createNote, salesBubbleId],
  );

  const updateExistingNote = useCallback(
    async (noteId: string, noteData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await updateNote({
          body: noteData,
          pathParams: { bubbleId: salesBubbleId, id: prospectId, noteId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update note");
      }
    },
    [prospectId, refetch, toast, updateNote, salesBubbleId],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await removeNote({
          pathParams: { bubbleId: salesBubbleId, id: prospectId, noteId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete note");
      }
    },
    [prospectId, refetch, toast, removeNote, salesBubbleId],
  );

  // Interaction management
  const addNewInteraction = useCallback(
    async (interactionData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await createInteraction({
          body: interactionData,
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add interaction");
      }
    },
    [prospectId, refetch, toast, createInteraction, salesBubbleId],
  );

  const updateExistingInteraction = useCallback(
    async (interactionId: string, interactionData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await updateInteraction({
          body: interactionData,
          pathParams: { bubbleId: salesBubbleId, id: prospectId, interactionId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update interaction");
      }
    },
    [prospectId, refetch, toast, updateInteraction, salesBubbleId],
  );

  const deleteInteraction = useCallback(
    async (interactionId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await removeInteraction({
          pathParams: { bubbleId: salesBubbleId, id: prospectId, interactionId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete interaction");
      }
    },
    [prospectId, refetch, toast, removeInteraction, salesBubbleId],
  );

  // Task management
  const addNewTask = useCallback(
    async (taskData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await addTask({
          body: taskData,
          pathParams: { bubbleId: salesBubbleId, id: prospectId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to add task");
      }
    },
    [prospectId, refetch, toast, addTask, salesBubbleId],
  );

  const updateExistingTask = useCallback(
    async (taskId: string, taskData: Record<string, unknown>) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await updateTask({
          body: taskData,
          pathParams: { bubbleId: salesBubbleId, taskId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to update task");
      }
    },
    [prospectId, refetch, toast, updateTask, salesBubbleId],
  );

  const removeTask = useCallback(
    async (taskId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await deleteTask({
          pathParams: { bubbleId: salesBubbleId, taskId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to delete task");
      }
    },
    [refetch, toast, deleteTask, salesBubbleId],
  );

  const completeExistingTask = useCallback(
    async (taskId: string) => {
      if (!salesBubbleId) {
        return;
      }

      try {
        await completeTask({
          pathParams: { bubbleId: salesBubbleId, taskId },
        });
        await refetch();
      } catch (err) {
        toast.error("Failed to complete task");
      }
    },
    [refetch, toast, completeTask, salesBubbleId],
  );

  return {
    // Contact actions
    addNewContact,

    // Interaction actions
    addNewInteraction,

    // Note actions
    addNewNote,

    // Task actions
    addNewTask,

    // Assignment actions
    assignUser,

    changeStage,

    completeExistingTask,

    deleteContact,

    deleteInteraction,

    deleteNote,

    error,

    isLoading,

    prospect,

    removeTask,

    unassignUser,

    updateExistingContact,

    updateExistingInteraction,

    updateExistingNote,

    updateExistingTask,
    // Header actions
    updateField: updateField as (
      field:
        | "name"
        | "type"
        | "estimatedValue"
        | "prospectType"
        | "expectedCloseDate"
        | "region"
        | "source"
        | "timeZone"
        | "website",
      value: string | number,
    ) => Promise<void>,
  };
};
